/**
 * 品質管理主組件 | Quality Management Main Component
 * 包含檢核項目管理、檢驗記錄、統計分析
 */

import { useState, useMemo } from 'react';
import { useQualityStore, useResourceStore } from '@/store';
import type { InspectionRecord } from '@/types/quality';

type ViewType = 'items' | 'incoming' | 'outgoing' | 'statistics';

export function QualityManagement() {
  const [viewType, setViewType] = useState<ViewType>('items');
  const [selectedProduct, setSelectedProduct] = useState<string>('all');

  const {
    inspectionItems,
    inspectionRecords,
    addInspectionItem,
    deleteInspectionItem,
    addInspectionRecord,
    getQualityStatistics,
  } = useQualityStore();

  const products = useResourceStore((state) => state.products);

  // 獲取產品選項
  const productOptions = useMemo(() => {
    return ['all', ...products.map((p) => p.id)];
  }, [products]);

  // 過濾檢核項目
  const filteredItems = useMemo(() => {
    if (selectedProduct === 'all') return inspectionItems;
    return inspectionItems.filter(
      (item: any) => !item.productId || item.productId === selectedProduct
    );
  }, [inspectionItems, selectedProduct]);

  // 統計資料
  const statistics = useMemo(() => getQualityStatistics(), [inspectionRecords]);

  // 處理新增檢核項目
  const handleAddItem = () => {
    const newItem = {
      code: `NEW${Date.now().toString().slice(-4)}`,
      name: '新檢核項目',
      category: '尺寸' as const,
      method: '量測' as const,
      standard: '待定義',
      isRequired: true,
      samplingRate: 100,
      criticalLevel: '一般' as const,
    };
    addInspectionItem(newItem);
  };

  // 格式化日期
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  return (
    <div className="space-y-4">
      {/* 標題與控制列 */}
      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-xl p-5 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* 左側：標題 */}
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/30">
              <span className="text-white text-lg">✓</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">品質管理</h2>
              <p className="text-sm text-slate-600">檢核項目與檢驗記錄</p>
            </div>
          </div>

          {/* 右側：視圖切換 */}
          <div className="flex bg-white border-2 border-emerald-200 rounded-lg shadow-sm overflow-hidden">
            {[
              { value: 'items', label: '檢核項目', icon: '📋' },
              { value: 'incoming', label: '來料檢驗', icon: '📥' },
              { value: 'outgoing', label: '出貨檢驗', icon: '📤' },
              { value: 'statistics', label: '統計分析', icon: '📊' },
            ].map((tab) => (
              <button
                key={tab.value}
                onClick={() => setViewType(tab.value as ViewType)}
                className={`px-4 py-2 text-sm font-semibold transition-all flex items-center gap-2 ${
                  viewType === tab.value
                    ? 'bg-gradient-to-r from-emerald-600 to-teal-500 text-white'
                    : 'text-emerald-700 hover:bg-emerald-50'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* 過濾器 */}
        <div className="mt-4 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-semibold text-slate-700">產品：</label>
            <select
              value={selectedProduct}
              onChange={(e) => setSelectedProduct(e.target.value)}
              className="px-3 py-1.5 bg-white border-2 border-emerald-200 text-emerald-700 rounded-lg text-sm font-semibold focus:ring-2 focus:ring-emerald-400 shadow-sm"
            >
              {productOptions.map((product) => (
                <option key={product} value={product}>
                  {product === 'all' ? '全部產品' : product}
                </option>
              ))}
            </select>
          </div>

          {/* 統計資訊 */}
          <div className="flex items-center gap-3 ml-auto">
            <div className="px-3 py-1.5 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-300 rounded-lg">
              <span className="text-xs font-bold text-emerald-700">
                檢核項目 {filteredItems.length} 個
              </span>
            </div>
            <div className="px-3 py-1.5 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-300 rounded-lg">
              <span className="text-xs font-bold text-blue-700">
                檢驗記錄 {inspectionRecords.length} 筆
              </span>
            </div>
            <div className="px-3 py-1.5 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-300 rounded-lg">
              <span className="text-xs font-bold text-green-700">
                合格率 {statistics.passRate}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* 內容區域 */}
      {viewType === 'items' && (
        <div className="bg-white border-2 border-slate-200 rounded-xl shadow-lg overflow-hidden">
          {/* 標題列 */}
          <div className="bg-gradient-to-r from-slate-50 to-white border-b-2 border-slate-200 px-6 py-4 flex items-center justify-between">
            <h3 className="text-lg font-bold text-slate-800">檢核項目清單</h3>
            <button
              onClick={handleAddItem}
              className="px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-700 hover:to-teal-600 text-white rounded-lg transition-all text-sm font-semibold shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40 active:scale-95"
            >
              ➕ 新增項目
            </button>
          </div>

          {/* 檢核項目表格 */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-slate-50 to-slate-100 border-b-2 border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">代碼</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">名稱</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">類別</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">方法</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">標準</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">重要度</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">抽樣率</th>
                  <th className="px-4 py-3 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">必檢</th>
                  <th className="px-4 py-3 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-12">
                      <div className="text-center">
                        <div className="text-6xl mb-4">📋</div>
                        <h3 className="text-xl font-bold text-slate-800 mb-2">沒有檢核項目資料</h3>
                        <p className="text-slate-600 mb-4 max-w-md mx-auto">
                          {inspectionItems.length === 0
                            ? '點擊「新增項目」建立第一個檢核項目'
                            : '沒有符合過濾條件的檢核項目'}
                        </p>
                        {selectedProduct !== 'all' && (
                          <button
                            onClick={() => setSelectedProduct('all')}
                            className="px-5 py-2.5 bg-gradient-to-r from-slate-100 to-slate-200 hover:from-slate-200 hover:to-slate-300 text-slate-700 rounded-lg transition-all text-sm font-semibold shadow-sm active:scale-95"
                          >
                            清除過濾
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredItems.map((item) => (
                    <tr key={item.id} className="hover:bg-emerald-50/50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm font-mono font-bold text-emerald-600">{item.code}</span>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-slate-700">{item.name}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-semibold border border-blue-200">
                          {item.category}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{item.method}</td>
                      <td className="px-4 py-3 text-sm text-slate-700">
                        <div>{item.standard}</div>
                        {item.tolerance && (
                          <div className="text-xs text-slate-500">公差: {item.tolerance}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-semibold border ${
                          item.criticalLevel === '關鍵'
                            ? 'bg-red-100 text-red-700 border-red-300'
                            : item.criticalLevel === '重要'
                            ? 'bg-amber-100 text-amber-700 border-amber-300'
                            : 'bg-slate-100 text-slate-700 border-slate-300'
                        }`}>
                          {item.criticalLevel}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {item.samplingRate ? `${item.samplingRate}%` : '-'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {item.isRequired ? (
                          <span className="inline-flex items-center justify-center w-6 h-6 bg-green-100 text-green-700 rounded-full border border-green-300">
                            ✓
                          </span>
                        ) : (
                          <span className="inline-flex items-center justify-center w-6 h-6 bg-slate-100 text-slate-400 rounded-full border border-slate-300">
                            -
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors" title="編輯檢核項目" aria-label="編輯檢核項目">
                            <span aria-hidden="true">✏️</span>
                            <span className="sr-only">編輯</span>
                          </button>
                          <button
                            onClick={() => deleteInspectionItem(item.id)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="刪除檢核項目"
                            aria-label="刪除檢核項目"
                          >
                            <span aria-hidden="true">🗑️</span>
                            <span className="sr-only">刪除</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {viewType === 'incoming' && (
        <div className="bg-white border-2 border-slate-200 rounded-xl shadow-lg overflow-hidden">
          {/* 標題列 */}
          <div className="bg-gradient-to-r from-blue-50 to-white border-b-2 border-blue-200 px-6 py-4 flex items-center justify-between">
            <h3 className="text-lg font-bold text-slate-800">來料檢驗記錄</h3>
            <button
              onClick={() => {
                // 新增來料檢驗記錄
                const newRecord = {
                  type: '來料檢驗' as const,
                  orderId: `TEST-${Date.now()}`,
                  batchNo: `BATCH-${Date.now().toString().slice(-4)}`,
                  inspectionDate: new Date().toISOString(),
                  inspectorId: 'QA001',
                  inspectorName: '品檢員 A',
                  sampleSize: 3,
                  qualifiedSize: 3,
                  defectiveSize: 0,
                  passRate: 100,
                  status: '合格' as const,
                  hasDefect: false,
                  items: filteredItems.slice(0, 3).map((item: any) => ({
                    inspectionItemId: item.id,
                    itemName: item.name,
                    standard: item.standard,
                    measuredValue: '合格',
                    isQualified: true,
                    notes: ''
                  }))
                };
                addInspectionRecord(newRecord);
              }}
              className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white rounded-lg transition-all text-sm font-semibold shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 active:scale-95"
            >
              ➕ 新增記錄
            </button>
          </div>

          {/* 來料檢驗列表 */}
          <div className="p-6">
            {inspectionRecords.filter(r => r.type === '來料檢驗').length === 0 ? (
              <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200 rounded-xl p-12 text-center shadow-sm">
                <div className="text-6xl mb-4">📥</div>
                <h3 className="text-xl font-bold text-slate-800 mb-2">沒有來料檢驗記錄</h3>
                <p className="text-slate-600 mb-6 max-w-md mx-auto">
                  點擊「新增記錄」按鈕建立第一筆來料檢驗記錄
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {inspectionRecords
                  .filter(r => r.type === '來料檢驗')
                  .map(record => (
                    <div key={record.id} className="bg-gradient-to-r from-blue-50 to-white border border-blue-200 rounded-xl p-5 shadow-sm">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-mono font-bold text-blue-600">{record.batchNo}</span>
                          <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold border border-blue-200">
                            來料檢驗
                          </span>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                            record.status === '合格'
                              ? 'bg-green-100 text-green-700 border-green-300'
                              : 'bg-amber-100 text-amber-700 border-amber-300'
                          }`}>
                            {record.status}
                          </span>
                        </div>
                        <div className="text-sm text-slate-600">
                          {formatDate(record.inspectionDate)}
                        </div>
                      </div>
                      <div className="text-sm text-slate-700">
                        <p>檢驗項目：{record.items.length} 項</p>
                        <p>合格率：<span className="font-bold text-green-600">{record.passRate}%</span></p>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      )}

      {viewType === 'outgoing' && (
        <div className="bg-white border-2 border-slate-200 rounded-xl shadow-lg overflow-hidden">
          {/* 標題列 */}
          <div className="bg-gradient-to-r from-green-50 to-white border-b-2 border-green-200 px-6 py-4 flex items-center justify-between">
            <h3 className="text-lg font-bold text-slate-800">出貨檢驗記錄</h3>
            <button
              onClick={() => {
                // 新增出貨檢驗記錄
                const newRecord = {
                  type: '出貨檢驗' as const,
                  orderId: `TEST-${Date.now()}`,
                  batchNo: `SHIP-${Date.now().toString().slice(-4)}`,
                  inspectionDate: new Date().toISOString(),
                  inspectorId: 'QA001',
                  inspectorName: '品檢員 A',
                  sampleSize: 3,
                  qualifiedSize: 3,
                  defectiveSize: 0,
                  passRate: 100,
                  status: '合格' as const,
                  hasDefect: false,
                  items: filteredItems.slice(0, 3).map(item => ({
                    inspectionItemId: item.id,
                    itemName: item.name,
                    standard: item.standard,
                    measuredValue: '合格',
                    isQualified: true,
                    notes: ''
                  }))
                };
                addInspectionRecord(newRecord);
              }}
              className="px-5 py-2.5 bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-700 hover:to-emerald-600 text-white rounded-lg transition-all text-sm font-semibold shadow-lg shadow-green-500/30 hover:shadow-xl hover:shadow-green-500/40 active:scale-95"
            >
              ➕ 新增記錄
            </button>
          </div>

          {/* 出貨檢驗列表 */}
          <div className="p-6">
            {inspectionRecords.filter(r => r.type === '出貨檢驗').length === 0 ? (
              <div className="bg-gradient-to-br from-green-50 to-white border-2 border-green-200 rounded-xl p-12 text-center shadow-sm">
                <div className="text-6xl mb-4">📤</div>
                <h3 className="text-xl font-bold text-slate-800 mb-2">沒有出貨檢驗記錄</h3>
                <p className="text-slate-600 mb-6 max-w-md mx-auto">
                  點擊「新增記錄」按鈕建立第一筆出貨檢驗記錄
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {inspectionRecords
                  .filter(r => r.type === '出貨檢驗')
                  .map(record => (
                    <div key={record.id} className="bg-gradient-to-r from-green-50 to-white border border-green-200 rounded-xl p-5 shadow-sm">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-mono font-bold text-green-600">{record.batchNo}</span>
                          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold border border-green-200">
                            出貨檢驗
                          </span>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                            record.status === '合格'
                              ? 'bg-green-100 text-green-700 border-green-300'
                              : 'bg-amber-100 text-amber-700 border-amber-300'
                          }`}>
                            {record.status}
                          </span>
                        </div>
                        <div className="text-sm text-slate-600">
                          {formatDate(record.inspectionDate)}
                        </div>
                      </div>
                      <div className="text-sm text-slate-700">
                        <p>檢驗項目：{record.items.length} 項</p>
                        <p>合格率：<span className="font-bold text-green-600">{record.passRate}%</span></p>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      )}

      {viewType === 'statistics' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* 總檢驗批次 */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="text-sm font-medium text-blue-700 mb-2">總檢驗批次</div>
            <div className="text-3xl font-black text-blue-600">{statistics.totalInspections}</div>
          </div>

          {/* 整體合格率 */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="text-sm font-medium text-green-700 mb-2">整體合格率</div>
            <div className="text-3xl font-black text-green-600">{statistics.passRate}%</div>
          </div>

          {/* 不良率 */}
          <div className="bg-gradient-to-br from-red-50 to-rose-50 border-2 border-red-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="text-sm font-medium text-red-700 mb-2">不良率</div>
            <div className="text-3xl font-black text-red-600">{statistics.defectRate}%</div>
          </div>

          {/* 主要不良類型 */}
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="text-sm font-medium text-amber-700 mb-2">主要不良類型</div>
            <div className="text-lg font-black text-amber-600">
              {statistics.topDefectTypes[0]?.defectType || '無'}
            </div>
            {statistics.topDefectTypes[0] && (
              <div className="text-xs text-amber-600 mt-1">
                {statistics.topDefectTypes[0].count} 次 ({statistics.topDefectTypes[0].percentage.toFixed(1)}%)
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
